/**
 * What a bound key is called, for every surface that says one.
 *
 * The one lookup between a binding and the words on screen: the settings rows, the engage card
 * legends and the interaction prompts all ask here, so no surface spells a letter by hand and a
 * rebind changes every one of them at once.
 *
 * Layout aware where the browser allows it. A binding stores `KeyboardEvent.code`, which names a
 * physical key, so on an AZERTY board the code `KeyW` prints Z; `navigator.keyboard` says what
 * each code prints and this shows that. The API is asynchronous and not everywhere, so the map is
 * fetched once in the background and until it lands, or on browsers without it, the fallback
 * wording in `text/keyText.ts` answers. Callers read per frame anyway, so the better answer
 * arrives without anyone subscribing.
 */

import { boundCodes } from '@/core/input/bindings/keyBindingStore';
import { KEY_UNBOUND, keyWord } from '@/text/keyText';
import { WEAPON_SLOT_ACTIONS } from '@/core/input/bindings/bindingContract';
import type { BindableActionId } from '@/core/input/bindings/bindingContract';

/** The experimental keyboard map API, stated so the probe below is typed rather than assumed. */
interface KeyboardLayoutApi {
  getLayoutMap(): Promise<ReadonlyMap<string, string>>;
}

let layout: ReadonlyMap<string, string> | null = null;
let requested = false;

function requestLayout(): void {
  if (requested) return;
  requested = true;
  // A structural probe rather than a bare cast into the wind: the property is read off a widened
  // view of `navigator`, then every use goes through the interface above. Browsers without the
  // API leave the fallback wording standing, which is the designed answer rather than an error.
  const holder: Navigator & { readonly keyboard?: KeyboardLayoutApi } = navigator;
  const keyboard = holder.keyboard;
  if (keyboard === undefined) return;
  void keyboard.getLayoutMap().then((map) => {
    layout = map;
  });
}

/** The legend word for one code: what the key prints on this layout, or the fallback wording. */
export function codeLabel(code: string): string {
  requestLayout();
  const printed = layout?.get(code);
  if (printed === undefined) return keyWord(code);
  // Single printable characters read as key caps: uppercase. Anything longer is already a name.
  return printed.length === 1 ? printed.toUpperCase() : printed;
}

/** What a prompt or a legend writes for an action: its first key's label, or a visible absence. */
export function actionKeyLabel(action: BindableActionId): string {
  const code = boundCodes(action)[0];
  return code === undefined ? KEY_UNBOUND : codeLabel(code);
}

/** Every key an action answers to, for the settings row that shows the synonyms. */
export function actionKeyLabels(action: BindableActionId): readonly string[] {
  return boundCodes(action).map(codeLabel);
}

/** The move keys as one legend cell: forward, left, back, right, the order hands learn them in. */
export function moveKeysLegend(): string {
  const order: readonly BindableActionId[] = ['moveForward', 'moveLeft', 'moveBack', 'moveRight'];
  return order.map(actionKeyLabel).join(' ');
}

/** The weapon row as one legend cell: the first and last slot keys, bridged. */
export function weaponRowLegend(): string {
  const first = WEAPON_SLOT_ACTIONS[0];
  const last = WEAPON_SLOT_ACTIONS[WEAPON_SLOT_ACTIONS.length - 1] ?? first;
  return `${actionKeyLabel(first)} - ${actionKeyLabel(last)}`;
}
